import React, { useState } from 'react';
import {
  Box,
  Grid,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  Chip,
  Stack,
  Paper
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  FilterList as FilterIcon
} from '@mui/icons-material';
import TaskCard from './TaskCard';

function TaskList({ tasks, onCreateTask, onEditTask, onDeleteTask, onStatusChange }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [filterPhase, setFilterPhase] = useState('all');
  const [filterAssignee, setFilterAssignee] = useState('all');

  // Extraire les phases et assignés uniques
  const phases = [...new Set(tasks.filter(t => t.phase).map(t => t.phase))];
  
  // Collecter tous les assignés uniques à partir de task.assignees (nouveau) et task.assignee_id (ancien)
  const assigneeMap = new Map();
  tasks.forEach(t => {
    if (t.assignees && t.assignees.length > 0) {
      t.assignees.forEach(a => assigneeMap.set(a.id, a.full_name || a.name));
    } else if (t.assignee_id) {
      assigneeMap.set(t.assignee_id, t.assignee_name);
    }
  });
  const assignees = Array.from(assigneeMap.entries()).map(([id, name]) => ({ id, name }));

  // Filtrer les tâches
  const filteredTasks = tasks.filter(task => {
    // Recherche par titre ou description
    const matchesSearch = !searchQuery ||
      task.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (task.description && task.description.toLowerCase().includes(searchQuery.toLowerCase()));

    // Filtre par statut
    const matchesStatus = filterStatus === 'all' || task.status === filterStatus;

    // Filtre par priorité
    const matchesPriority = filterPriority === 'all' || task.priority === filterPriority;

    // Filtre par phase
    const matchesPhase = filterPhase === 'all' || task.phase === filterPhase;

    // Filtre par assigné
    const matchesAssignee = filterAssignee === 'all' || 
      task.assignee_id === parseInt(filterAssignee) || 
      (task.assignees && task.assignees.some(a => a.id === parseInt(filterAssignee)));

    return matchesSearch && matchesStatus && matchesPriority && matchesPhase && matchesAssignee;
  });

  // Grouper par statut ou phase
  const groupedTasks = {
    not_started: filteredTasks.filter(t => t.status === 'not_started'),
    in_progress: filteredTasks.filter(t => t.status === 'in_progress'),
    completed: filteredTasks.filter(t => t.status === 'completed'),
    blocked: filteredTasks.filter(t => t.status === 'blocked')
  };

  const getStatusLabel = (status) => {
    const labels = {
      not_started: 'À faire',
      in_progress: 'En cours',
      completed: 'Terminé',
      blocked: 'Bloqué'
    };
    return labels[status];
  };

  const resetFilters = () => {
    setSearchQuery('');
    setFilterStatus('all');
    setFilterPriority('all');
    setFilterPhase('all');
    setFilterAssignee('all');
  };

  const hasActiveFilters = searchQuery || filterStatus !== 'all' || filterPriority !== 'all' ||
                          filterPhase !== 'all' || filterAssignee !== 'all';

  return (
    <Box>
      {/* En-tête et Filtres */}
      <Paper sx={{ p: 2, mb: 3, bgcolor: 'background.paper' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6">
            Tâches ({filteredTasks.length})
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={onCreateTask}
          >
            Nouvelle tâche
          </Button>
        </Box>

        {/* Filtres */}
        <Box>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                placeholder="Rechercher une tâche..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={6} sm={3} md={1.5}>
              <FormControl fullWidth size="small">
                <InputLabel>Statut</InputLabel>
                <Select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  label="Statut"
                >
                  <MenuItem value="all">Tous</MenuItem>
                  <MenuItem value="not_started">À faire</MenuItem>
                  <MenuItem value="in_progress">En cours</MenuItem>
                  <MenuItem value="completed">Terminé</MenuItem>
                  <MenuItem value="blocked">Bloqué</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} sm={3} md={1.5}>
              <FormControl fullWidth size="small">
                <InputLabel>Priorité</InputLabel>
                <Select
                  value={filterPriority}
                  onChange={(e) => setFilterPriority(e.target.value)}
                  label="Priorité"
                >
                  <MenuItem value="all">Toutes</MenuItem>
                  <MenuItem value="low">Basse</MenuItem>
                  <MenuItem value="medium">Moyenne</MenuItem>
                  <MenuItem value="high">Haute</MenuItem>
                  <MenuItem value="urgent">Urgente</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {phases.length > 0 && (
              <Grid item xs={6} sm={3} md={1.5}>
                <FormControl fullWidth size="small">
                  <InputLabel>Phase</InputLabel>
                  <Select
                    value={filterPhase}
                    onChange={(e) => setFilterPhase(e.target.value)}
                    label="Phase"
                  >
                    <MenuItem value="all">Toutes</MenuItem>
                    {phases.map((phase) => (
                      <MenuItem key={phase} value={phase}>{phase}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
            {assignees.length > 0 && (
              <Grid item xs={6} sm={3} md={1.5}>
                <FormControl fullWidth size="small">
                  <InputLabel>Assigné</InputLabel>
                  <Select
                    value={filterAssignee}
                    onChange={(e) => setFilterAssignee(e.target.value)}
                    label="Assigné"
                  >
                    <MenuItem value="all">Tous</MenuItem>
                    {assignees.map((assignee) => (
                      <MenuItem key={assignee.id} value={assignee.id}>
                        {assignee.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
          </Grid>
          {hasActiveFilters && (
            <Box sx={{ mt: 1 }}>
              <Button size="small" onClick={resetFilters}>
                Réinitialiser les filtres
              </Button>
            </Box>
          )}
        </Box>
      </Paper>

      {/* Liste des tâches groupées par statut */}
      {filteredTasks.length > 0 ? (
        <Box>
          {Object.entries(groupedTasks).map(([status, statusTasks]) => (
            statusTasks.length > 0 && (
              <Box key={status} sx={{ mb: 4 }}>
                <Paper 
                  elevation={0}
                  sx={{ 
                    display: 'inline-flex', 
                    alignItems: 'center', 
                    mb: 2, 
                    p: 1.5, 
                    bgcolor: 'background.paper',
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'divider'
                  }}
                >
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                    {getStatusLabel(status)}
                  </Typography>
                  <Chip
                    label={statusTasks.length}
                    size="small"
                    sx={{ ml: 1 }}
                  />
                </Paper>
                <Grid container spacing={2}>
                  {statusTasks.map((task) => (
                    <Grid item xs={12} sm={6} md={4} key={task.id}>
                      <TaskCard
                        task={task}
                        onEdit={onEditTask}
                        onDelete={onDeleteTask}
                        onStatusChange={onStatusChange}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )
          ))}
        </Box>
      ) : (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {hasActiveFilters ? 'Aucune tâche ne correspond à vos critères' : 'Aucune tâche pour le moment'}
          </Typography>
          {!hasActiveFilters && (
            <Button
              variant="outlined"
              size="large"
              startIcon={<AddIcon />}
              onClick={onCreateTask}
              sx={{ mt: 2 }}
            >
              Créer votre première tâche
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
}

export default TaskList;
